import { useSearchParams, type SetURLSearchParams } from "react-router";
import {
  type PaginationState,
  type SortingState,
  type Updater,
} from "@tanstack/react-table";
import { type DocumentVersionSummary } from "~/api/types";
import { QueryParams, useDocumentVersions } from "~/api/v2/useDocumentVersions";
import { filterDocumentVersions } from "~/helpers/documentFilters";
import { allPageSizeParamValue } from "~/helpers/pagination";
import { useDocumentVersionFilters } from "./useDocumentVersionFilters";

const sortParam = "sort";
const pageParam = "page";
const pageSizeParam = "pageSize";
const defaultPageSize = 20;

const resolveUpdater = <TValue>(
  updater: Updater<TValue>,
  previous: TValue,
): TValue =>
  typeof updater === "function"
    ? (updater as (old: TValue) => TValue)(previous)
    : updater;

const defaultSorting: SortingState = [{ id: "title", desc: false }];

const parseSortingFromParams = (
  searchParams: URLSearchParams,
): SortingState => {
  const sortValue = searchParams.get(sortParam);
  if (!sortValue) return defaultSorting;
  const [id, direction] = sortValue.split(":");
  if (!id || (direction !== "asc" && direction !== "desc"))
    return defaultSorting;
  return [{ id, desc: direction === "desc" }];
};

const parsePaginationFromParams = (
  searchParams: URLSearchParams,
): PaginationState => {
  const page = Number(searchParams.get(pageParam) ?? "1");
  const pageSize = Number(searchParams.get(pageSizeParam) ?? defaultPageSize);

  const safePage = Number.isFinite(page) && page > 0 ? Math.floor(page) : 1;
  const safePageSize =
    Number.isFinite(pageSize) && pageSize > 0
      ? Math.floor(pageSize)
      : defaultPageSize;

  return {
    pageIndex: safePage - 1,
    pageSize: safePageSize,
  };
};

const updateSearchParams = (
  searchParams: URLSearchParams,
  setSearchParams: SetURLSearchParams,
  apply: (next: URLSearchParams) => void,
) => {
  const next = new URLSearchParams(searchParams);
  apply(next);
  setSearchParams(next);
};

export const useDocumentVersionsListModel = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const titleSearch = searchParams.get(QueryParams.Title);
  const { data, isLoading, mutate } = useDocumentVersions({
    includeWorkflow: true,
    titleSearch,
  });

  const documentVersions = data ?? [];
  const filters = useDocumentVersionFilters({
    documentVersions,
    searchParams,
    setSearchParams,
  });
  const sorting = parseSortingFromParams(searchParams);
  const filteredDocumentVersions: DocumentVersionSummary[] =
    filterDocumentVersions(documentVersions, filters.activeFilters);
  const isAllPageSizeMode =
    searchParams.get(pageSizeParam) === allPageSizeParamValue;
  const parsedPagination = parsePaginationFromParams(searchParams);
  const pagination = isAllPageSizeMode
    ? {
        pageIndex: 0,
        pageSize: Math.max(filteredDocumentVersions.length, 1),
      }
    : parsedPagination;

  const handleSortingChange = (updater: Updater<SortingState>) => {
    const nextSorting = resolveUpdater(updater, sorting);

    updateSearchParams(searchParams, setSearchParams, (next) => {
      const firstSort = nextSorting[0];
      if (firstSort) {
        next.set(
          sortParam,
          `${firstSort.id}:${firstSort.desc ? "desc" : "asc"}`,
        );
      } else {
        next.delete(sortParam);
      }
      next.set(pageParam, "1");
    });
  };

  const handlePaginationChange = (updater: Updater<PaginationState>) => {
    if (isAllPageSizeMode) {
      return;
    }

    const nextPagination = resolveUpdater(updater, pagination);

    updateSearchParams(searchParams, setSearchParams, (next) => {
      next.set(pageParam, (nextPagination.pageIndex + 1).toString());
      next.set(pageSizeParam, nextPagination.pageSize.toString());
    });
  };

  const handleTitleSearchChange = (value: string) => {
    updateSearchParams(searchParams, setSearchParams, (next) => {
      if (value) {
        next.set(QueryParams.Title, value);
      } else {
        next.delete(QueryParams.Title);
      }
      next.set(pageParam, "1");
    });
  };

  const handlePageSizeChange = (value: string) => {
    updateSearchParams(searchParams, setSearchParams, (next) => {
      next.set(pageSizeParam, value);
      next.set(pageParam, "1");
    });
  };

  return {
    documentVersions,
    filteredDocumentVersions,
    filters,
    hasData: data !== undefined,
    isAllPageSizeMode,
    isLoading,
    mutate,
    pagination,
    sorting,
    titleSearch: titleSearch ?? "",
    handlePageSizeChange,
    handlePaginationChange,
    handleSortingChange,
    handleTitleSearchChange,
  };
};

export type DocumentVersionsListModel = ReturnType<
  typeof useDocumentVersionsListModel
>;
